// What the partner-brand CREATE wizard needs to know before it asks "who is
// this site for?" — composed from reads that already exist, with no new
// endpoint.
//
// The question only arises when the wizard was launched from a BROKER: a broker
// may sit under a firm, so "their own site" and "the firm's shared site" are
// genuinely different products with a privacy consequence between them. A
// brokerage launch and the standalone ownerless path have no such fork, so this
// hook stays entirely inert for them — every query is disabled and the wizard
// behaves exactly as it did before.
//
// Four sources, in this order:
//   1. the record-scoped site read (own / inherited / none / ambiguous-firm);
//   2. the broker detail, for the covering firm — the read model names the firm
//      only when a site was actually INHERITED, so "has a firm that has no site
//      yet" is invisible without it. Same SelectOwningFirm rule underneath, so
//      this can never disagree with what would resolve;
//   3. the firm's own brand, prefetched only when a personal site would copy
//      its presentation fields; and
//   4. when the broker is multi-homed and the staff member has PICKED one of the
//      candidate firms, that brokerage's own site read — which is what turns the
//      ambiguity into an ordinary firm-no-site / firm-has-site fork. Picking a
//      firm RESOLVES the ambiguity; it is never a third terminal branch.

import { useQuery } from "@tanstack/react-query";
import { brokersApi } from "@/api/brokers";
import { brokerKeys } from "@/hooks/brokers/brokerKeys";
import { getBrokerDisplayName } from "@/lib/brokerDisplay";
import type { PartnerBrand } from "@/api/partnerBrands";
import type { PrequalSiteCard } from "@/api/prequalSiteAdmin";
import { useBrokerPrequalSite, useBrokeragePrequalSite } from "./usePrequalSiteAdmin";
import { usePartnerBrand } from "./usePartnerBrands";

export interface DecisionFirm {
  id: string;
  name: string;
}

export type PartnerBrandLaunchDecision =
  /** Not launched from a broker, or the broker has no covering firm — there is
   *  nothing to fork on. The wizard runs unchanged. */
  | { mode: "none" }
  /** Still resolving. The wizard holds the first step rather than letting a
   *  personal site be created before the shared option was ever offered. */
  | { mode: "loading" }
  /** The lookup failed. No fork: a shared site is never offered against a firm
   *  we could not verify. Creating a personal site still works. */
  | { mode: "unavailable" }
  /** The broker's firm exists and has NO site — the real fork. */
  | { mode: "firm-no-site"; brokerName: string; firm: DecisionFirm }
  /** The broker inherits their firm's site — keep using it, or split off. */
  | {
      mode: "firm-has-site";
      brokerName: string;
      firm: DecisionFirm;
      site: PrequalSiteCard;
      /** The firm's brand, for the presentation-field copy. */
      firmBrand: PartnerBrand | undefined;
      firmBrandLoading: boolean;
    }
  /** Multi-homed with no primary affiliation: no single firm a shared site
   *  could belong to, so the staff member picks one (or declines and creates a
   *  personal site). Named explicitly — never folded into "no firm".
   *  `candidates` is empty when the server could not name the firms, in which
   *  case this stays the advisory dead-end it has always been. */
  | { mode: "ambiguous-firm"; brokerName: string; candidates: DecisionFirm[] }
  /** The broker already owns a site. One site per owner is enforced server-side,
   *  so a second create for them would be refused. */
  | { mode: "owns-site"; brokerName: string; site: PrequalSiteCard };

/** True when the decision needs a choice from the staff member (as opposed to
 *  an advisory notice, or nothing at all). An ambiguous affiliation qualifies
 *  only when there are candidate firms to pick BETWEEN — with none named it is
 *  still the advisory dead-end, and the wizard runs on unchanged. */
export function decisionNeedsChoice(d: PartnerBrandLaunchDecision): boolean {
  if (d.mode === "ambiguous-firm") return d.candidates.length > 0;
  return d.mode === "firm-no-site" || d.mode === "firm-has-site";
}

export function usePartnerBrandLaunchDecision(
  launchBrokerId: string | null,
  fallbackBrokerName?: string | null,
  /** A candidate firm picked out of an ambiguous affiliation. Once set, the
   *  decision resolves as if that firm were the covering one. */
  chosenFirmId?: string | null,
): PartnerBrandLaunchDecision {
  const enabled = !!launchBrokerId;

  const site = useBrokerPrequalSite(launchBrokerId ?? "");
  const broker = useQuery({
    queryKey: brokerKeys.detail(launchBrokerId ?? ""),
    queryFn: () => brokersApi.get(launchBrokerId!),
    enabled,
  });

  // The picked firm's own site — the only way to know whether resolving the
  // ambiguity lands on firm-has-site or firm-no-site. Inert until a pick.
  const chosenFirm = useBrokeragePrequalSite(chosenFirmId ?? "");
  const chosenFirmSite =
    chosenFirm.data?.resolution === "Own" ? (chosenFirm.data.site ?? null) : null;

  // Only the split-off branch reads the firm's brand, and only when a firm site
  // exists — either inherited, or the one the picked firm already runs. A broker
  // can never be in both states at once, so this is a single read.
  const firmBrandId =
    site.data?.resolution === "Inherited"
      ? (site.data.site?.brandId ?? null)
      : (chosenFirmSite?.brandId ?? null);
  const firmBrand = usePartnerBrand(firmBrandId ?? undefined);

  if (!enabled) return { mode: "none" };
  if (site.isLoading || broker.isLoading) return { mode: "loading" };
  if (site.isError || !site.data) return { mode: "unavailable" };

  const brokerName =
    (broker.data ? getBrokerDisplayName(broker.data) : null) ||
    fallbackBrokerName ||
    "this broker";

  const data = site.data;

  if (data.resolution === "AmbiguousFirm") {
    const candidates: DecisionFirm[] = (data.candidateFirms ?? []).map((c) => ({
      id: c.brokerageId,
      name: c.name,
    }));
    const picked = chosenFirmId ? candidates.find((c) => c.id === chosenFirmId) : undefined;
    // No pick yet (or a pick that is not on the list — never trust it): the
    // ambiguity stands.
    if (!picked) return { mode: "ambiguous-firm", brokerName, candidates };

    if (chosenFirm.isLoading) return { mode: "loading" };
    // A failed read must not be reported as "that firm has no site" — offering
    // to create a shared site for a firm we could not verify is the mistake.
    if (chosenFirm.isError || !chosenFirm.data) return { mode: "unavailable" };

    return chosenFirmSite
      ? {
          mode: "firm-has-site",
          brokerName,
          firm: picked,
          site: chosenFirmSite,
          firmBrand: firmBrand.data,
          firmBrandLoading: firmBrand.isLoading,
        }
      : { mode: "firm-no-site", brokerName, firm: picked };
  }

  if (data.resolution === "Own" && data.site) {
    return { mode: "owns-site", brokerName, site: data.site };
  }

  if (data.resolution === "Inherited" && data.site && data.inheritedFromBrokerageId) {
    return {
      mode: "firm-has-site",
      brokerName,
      firm: {
        id: data.inheritedFromBrokerageId,
        name: data.inheritedFromBrokerageName || "their brokerage",
      },
      site: data.site,
      firmBrand: firmBrand.data,
      firmBrandLoading: firmBrand.isLoading,
    };
  }

  // Nothing resolved and the firm is unambiguous: the fork exists only if there
  // IS a firm. A broker with none has nobody to share a site with — but a
  // FAILED broker read is not the same thing as no firm, and must not be
  // reported as one.
  if (broker.isError || !broker.data) return { mode: "unavailable" };

  const firmId = broker.data.affiliatedBrokerageId;
  if (firmId) {
    return {
      mode: "firm-no-site",
      brokerName,
      firm: { id: firmId, name: broker.data.affiliatedBrokerageName || "their brokerage" },
    };
  }

  return { mode: "none" };
}
